/**
 * GlobalDictionaryFormModal — panel ITE, „Globalne słowniki".
 *
 * Modal create/edit dla słownika globalnego (mirror `GlobalDocumentTypeFormModal`).
 *
 * Kod jest edytowalny wyłącznie dla szkiców (`status==='draft'`) — po publikacji
 * backend odrzuca zmianę kodu (409 CODE_IMMUTABLE), więc pole jest `disabled`
 * z podpowiedzią zamiast pozwalać na próbę zapisu.
 *
 * Etap 10.5 (ADR-0066 §7/§11): przełącznik rodzaju pozycji (ręczny / zewnętrzny). Dla
 * `external` DWA warianty źródła (Faza 2d, ADR-0068):
 *  - „Encja z rejestru" — picker zarejestrowanego źródła (`GET /api/admin/dictionary-sources`)
 *    + `ExternalSourceInfoPanel` (read-only nazwa/opis/parametry; panel ITE nie widzi
 *    realnych pozycji, bo te żyją w bazie tenanta),
 *  - „Zapytanie SQL" — textarea SELECT (mono) + edytor listy zadeklarowanych parametrów
 *    (nazwa + typ + wymagany; etap 1 modelu dwuetapowego) + sekcja „Testuj": ITE wybiera
 *    tenanta (lista `GET /api/admin/tenants`), opcjonalnie podaje przykładowe wartości
 *    parametrów i odpala `POST /api/admin/global-dictionaries/test-sql` — wynik jako grid
 *    pierwszych wierszy + rowCount + nota o obcięciu; kody błędów walidacji
 *    (`SQL_NOT_SELECT`/`SQL_MULTIPLE_STATEMENTS`/`SQL_PARAM_UNDECLARED`) mapowane na
 *    czytelne komunikaty.
 *  Warianty są ROZŁĄCZNE (XOR jak CHECK w bazie): wybór SQL czyści `sourceKey`, wybór
 *  rejestru czyści `sourceSql`/`sourceParams`.
 * Rodzaj i źródło są zamrożone tym samym flagiem co kod (`codeLocked` — czyli po
 * publikacji), analogicznie do niezmienialności kodu: zmiana struktury słownika po
 * opublikowaniu jest wyłączona.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { ExternalSourceInfoPanel } from '@/components/dictionaries/ExternalSourceInfoPanel';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { InfoHint } from '@/components/InfoHint';
import { Input } from '@/components/Input';
import { InsertExampleButton } from '@/components/InsertExampleButton';
import { Modal } from '@/components/Modal';
import { RadioGroup } from '@/components/RadioGroup';
import { SqlField } from '@/components/ui/SqlField';
import {
  DICTIONARY_SOURCE_PARAM_TYPES,
  type DictionarySourceParam,
  type DictionarySourceParamType,
  type GlobalDictionaryDisplayMode,
  type GlobalDictionaryFormValues,
  type GlobalDictionarySourceKind,
  useAdminTenantsLite,
  useDictionarySources,
  useTestGlobalDictionarySql,
} from '@/lib/global-dictionaries/api';
import { buildSqlParamCompletions } from '@/lib/sql-editor/params';
import { useAdminTenantSqlSchema } from '@/lib/sql-editor/schema';
import { useSqlTestDiagnostics } from '@/lib/sql-editor/test-error';

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: GlobalDictionaryFormValues;
  /** Kod (i rodzaj/źródło pozycji) są zamrożone gdy edytowany słownik ma status inny niż 'draft'. */
  codeLocked?: boolean;
  saving?: boolean;
  /** Błędy pól zmapowane z odpowiedzi 409/422 backendu. */
  serverErrors?: Record<string, string>;
  onSubmit: (values: GlobalDictionaryFormValues) => void;
  onClose: () => void;
}

export function emptyGlobalDictionaryFormValues(): GlobalDictionaryFormValues {
  return {
    code: '',
    name: '',
    description: '',
    displayMode: 'label',
    sortOrder: '',
    sourceKind: 'manual',
    sourceKey: null,
    sourceSql: null,
    sourceParams: [],
  };
}

const DISPLAY_MODES: GlobalDictionaryDisplayMode[] = ['label', 'code', 'code_label'];

/** Wariant źródła zewnętrznego — rejestr encji vs zapytanie SQL (XOR, Faza 2d). */
type SourceVariant = 'registry' | 'sql';

/** Nazwa parametru SQL — identyfikator jak w `:nazwa` (mirror walidatora backendu). */
const PARAM_NAME_REGEX = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

/**
 * Mapuje kod błędu z `POST .../test-sql` na klucz i18n czytelnego komunikatu.
 * Nieznany kod → generyczny komunikat testu.
 */
function testSqlErrorKey(code: string | undefined): string {
  switch (code) {
    case 'SQL_NOT_SELECT':
      return 'admin.globalDictionaries.form.sqlErrors.notSelect';
    case 'SQL_MULTIPLE_STATEMENTS':
      return 'admin.globalDictionaries.form.sqlErrors.multipleStatements';
    case 'SQL_PARAM_UNDECLARED':
      return 'admin.globalDictionaries.form.sqlErrors.paramUndeclared';
    case 'EXTERNAL_PARAM_MISSING':
      return 'admin.globalDictionaries.form.sqlErrors.paramMissing';
    case 'EXTERNAL_PARAM_INVALID':
      return 'admin.globalDictionaries.form.sqlErrors.paramInvalid';
    default:
      return 'admin.globalDictionaries.form.sqlErrors.generic';
  }
}

export function GlobalDictionaryFormModal({
  open,
  mode,
  initialValues,
  codeLocked,
  saving,
  serverErrors,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState(initialValues.code);
  const [name, setName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description);
  const [displayMode, setDisplayMode] = useState<GlobalDictionaryDisplayMode>(
    initialValues.displayMode,
  );
  const [sortOrder, setSortOrder] = useState(initialValues.sortOrder);
  const [sourceKind, setSourceKind] = useState<GlobalDictionarySourceKind>(
    initialValues.sourceKind ?? 'manual',
  );
  const [sourceKey, setSourceKey] = useState<string | null>(initialValues.sourceKey ?? null);
  // Wariant SQL (Faza 2d) — treść SELECT + deklaracje parametrów (etap 1).
  const [sourceVariant, setSourceVariant] = useState<SourceVariant>(
    initialValues.sourceSql ? 'sql' : 'registry',
  );
  const [sourceSql, setSourceSql] = useState(initialValues.sourceSql ?? '');
  const [sourceParams, setSourceParams] = useState<DictionarySourceParam[]>(
    initialValues.sourceParams ?? [],
  );
  // Sekcja „Testuj" — tenant do próby + przykładowe wartości parametrów (nazwa → tekst).
  const [testTenantId, setTestTenantId] = useState('');
  const [testParamValues, setTestParamValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const sourcesQuery = useDictionarySources();
  const sources = sourcesQuery.data ?? [];
  const selectedSource = sources.find((source) => source.key === sourceKey) ?? null;

  const sqlVariantActive = sourceKind === 'external' && sourceVariant === 'sql';
  const tenantsQuery = useAdminTenantsLite(open && sqlVariantActive);
  const tenants = tenantsQuery.data ?? [];
  const testMutation = useTestGlobalDictionarySql();
  const sqlSchema = useAdminTenantSqlSchema(open && sqlVariantActive ? testTenantId : null);
  const sqlDiagnostics = useSqlTestDiagnostics(sourceSql, testMutation);
  const sqlParams = useMemo(() => buildSqlParamCompletions(t, [], sourceParams), [t, sourceParams]);

  useEffect(() => {
    if (open) {
      setCode(initialValues.code);
      setName(initialValues.name);
      setDescription(initialValues.description);
      setDisplayMode(initialValues.displayMode);
      setSortOrder(initialValues.sortOrder);
      setSourceKind(initialValues.sourceKind ?? 'manual');
      setSourceKey(initialValues.sourceKey ?? null);
      setSourceVariant(initialValues.sourceSql ? 'sql' : 'registry');
      setSourceSql(initialValues.sourceSql ?? '');
      setSourceParams(initialValues.sourceParams ?? []);
      setTestTenantId('');
      setTestParamValues({});
      testMutation.reset();
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (serverErrors) {
      setErrors((prev) => ({ ...prev, ...serverErrors }));
    }
  }, [serverErrors]);

  // „Zmieniono" dla zabezpieczenia zamknięcia: tryb wyświetlania, rodzaj i źródło pozycji oraz
  // parametry SQL wybiera się kliknięciem (bez zdarzeń wpisywania), więc porównujemy wartości
  // formularza ze stanem startowym. Tenant i wartości próbne sekcji „Testuj" to nie dane słownika.
  const isDirty =
    JSON.stringify([
      code,
      name,
      description,
      displayMode,
      sortOrder,
      sourceKind,
      sourceKey,
      sourceVariant,
      sourceSql,
      sourceParams,
    ]) !==
    JSON.stringify([
      initialValues.code,
      initialValues.name,
      initialValues.description,
      initialValues.displayMode,
      initialValues.sortOrder,
      initialValues.sourceKind ?? 'manual',
      initialValues.sourceKey ?? null,
      initialValues.sourceSql ? 'sql' : 'registry',
      initialValues.sourceSql ?? '',
      initialValues.sourceParams ?? [],
    ]);

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!code.trim()) {
      next.code = t('admin.globalDictionaries.form.errors.codeRequired');
    } else if (code.trim().length > 50) {
      next.code = t('admin.globalDictionaries.form.errors.codeLength');
    } else if (!/^[a-z][a-z0-9_]*$/.test(code.trim())) {
      next.code = t('admin.globalDictionaries.form.errors.codeFormat');
    }
    if (!name.trim()) {
      next.name = t('admin.globalDictionaries.form.errors.nameRequired');
    } else if (name.trim().length > 100) {
      next.name = t('admin.globalDictionaries.form.errors.nameLength');
    }
    if (description.length > 500) {
      next.description = t('admin.globalDictionaries.form.errors.descriptionLength');
    }
    if (sortOrder !== '' && !/^-?\d+$/.test(sortOrder.trim())) {
      next.sortOrder = t('admin.globalDictionaries.form.errors.sortOrderInvalid');
    }
    if (sqlVariantActive) {
      const names = sourceParams.map((p) => p.name.trim());
      if (names.some((n) => !PARAM_NAME_REGEX.test(n))) {
        next.sourceParams = t('admin.globalDictionaries.form.errors.sqlParamNameInvalid');
      } else if (new Set(names.map((n) => n.toLowerCase())).size !== names.length) {
        next.sourceParams = t('admin.globalDictionaries.form.errors.sqlParamNameDuplicate');
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({
      code: code.trim(),
      name: name.trim(),
      description: description.trim(),
      displayMode,
      sortOrder: sortOrder.trim(),
      sourceKind,
      // XOR (Faza 2d): aktywny wariant wygrywa, drugi jest zerowany.
      sourceKey: sourceKind === 'external' && sourceVariant === 'registry' ? sourceKey : null,
      sourceSql: sqlVariantActive && sourceSql.trim() ? sourceSql.trim() : null,
      sourceParams: sqlVariantActive
        ? sourceParams.map((p) => ({ ...p, name: p.name.trim() }))
        : [],
    });
  }

  function handleVariantChange(variant: SourceVariant) {
    setSourceVariant(variant);
    // XOR: wybór SQL czyści źródło z rejestru; wybór rejestru czyści SQL + parametry.
    if (variant === 'sql') {
      setSourceKey(null);
    } else {
      setSourceSql('');
      setSourceParams([]);
      testMutation.reset();
    }
  }

  function updateParam(index: number, patch: Partial<DictionarySourceParam>) {
    setSourceParams((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  }

  function removeParam(index: number) {
    setSourceParams((prev) => prev.filter((_, i) => i !== index));
  }

  function handleTest() {
    if (!testTenantId || !sourceSql.trim()) return;
    const paramValues: Record<string, unknown> = {};
    for (const param of sourceParams) {
      const raw = testParamValues[param.name];
      if (raw !== undefined && raw !== '') paramValues[param.name] = raw;
    }
    testMutation.mutate({
      sourceSql: sourceSql.trim(),
      sourceParams: sourceParams.map((p) => ({ ...p, name: p.name.trim() })),
      tenantId: testTenantId,
      ...(Object.keys(paramValues).length > 0 ? { paramValues } : {}),
    });
  }

  const testError = testMutation.isError
    ? (() => {
        const err = testMutation.error;
        const data =
          isAxiosError(err) && err.response?.data && typeof err.response.data === 'object'
            ? (err.response.data as { error?: string; paramName?: string })
            : null;
        return t(testSqlErrorKey(data?.error ?? undefined), { paramName: data?.paramName ?? '' });
      })()
    : null;
  const testResult = testMutation.data ?? null;

  const titleKey =
    mode === 'create'
      ? 'admin.globalDictionaries.form.titleCreate'
      : 'admin.globalDictionaries.form.titleEdit';
  // Kod ORAZ rodzaj/źródło pozycji dzielą jeden zamek — obie rzeczy definiują strukturę
  // słownika i backend zamraża `code` po publikacji (409 CODE_IMMUTABLE); zmiana rodzaju
  // pozycji na opublikowanym słowniku nie jest wspierana (brak DoD/ADR na taki przepływ),
  // więc UI blokuje ją tym samym flagiem zamiast zgadywać niezweryfikowaną semantykę PUT.
  const isStructureLocked = mode === 'edit' && codeLocked === true;
  const canSubmitSource =
    sourceKind === 'manual' ||
    (sourceVariant === 'registry' ? sourceKey !== null : sourceSql.trim().length > 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(titleKey)}
      // Słownik SQL: szerokie okno — dane słownika w dwóch kolumnach, pod nimi pole SQL na całą
      // szerokość, na dole parametry obok „Testuj” (bez przewijania); pozostałe tryby zostają wąskie.
      size={sqlVariantActive ? '2xl' : 'md'}
      testId="global-dictionary-form-modal"
      guardUnsaved
      dirty={isDirty}
      // W trakcie zapisu tło i Esc nie zamykają okna („Anuluj" jest wyłączone; X w nagłówku zostaje).
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            data-testid="global-dictionary-form-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={saving || !canSubmitSource}
            loading={saving}
            data-testid="global-dictionary-form-submit"
          >
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </>
      }
    >
      <div
        className={
          sqlVariantActive
            ? 'grid grid-cols-1 items-start gap-x-6 gap-y-3 lg:grid-cols-2'
            : 'space-y-3'
        }
        data-testid="global-dictionary-form-layout"
      >
        <div className="space-y-3">
          <FormField
            label={t('admin.globalDictionaries.form.code')}
            error={errors.code}
            hint={
              isStructureLocked ? t('admin.globalDictionaries.form.codeImmutableHint') : undefined
            }
          >
            <Input
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setErrors((prev) => ({ ...prev, code: '' }));
              }}
              disabled={isStructureLocked}
              placeholder={t('admin.globalDictionaries.form.codePlaceholder')}
              data-testid="global-dictionary-form-code"
            />
          </FormField>
          <FormField label={t('admin.globalDictionaries.form.name')} error={errors.name}>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setErrors((prev) => ({ ...prev, name: '' }));
              }}
              placeholder={t('admin.globalDictionaries.form.namePlaceholder')}
              data-testid="global-dictionary-form-name"
            />
          </FormField>
          <FormField
            label={t('admin.globalDictionaries.form.description')}
            error={errors.description}
          >
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('admin.globalDictionaries.form.descriptionPlaceholder')}
              rows={2}
              className="w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary"
              data-testid="global-dictionary-form-description"
            />
          </FormField>
          <FormField label={t('admin.globalDictionaries.form.displayMode')}>
            <Dropdown
              value={displayMode}
              onChange={(value) => setDisplayMode(value as GlobalDictionaryDisplayMode)}
              options={DISPLAY_MODES.map((mode) => ({
                value: mode,
                label: t(`admin.globalDictionaries.displayMode.${mode}`),
              }))}
              data-testid="global-dictionary-form-display-mode"
            />
          </FormField>
        </div>

        <div className="space-y-3">
          <FormField label={t('admin.globalDictionaries.form.sortOrder')} error={errors.sortOrder}>
            <Input
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              placeholder={t('admin.globalDictionaries.form.sortOrderPlaceholder')}
              data-testid="global-dictionary-form-sort-order"
            />
            <InfoHint>{t('common.sortOrderInfo')}</InfoHint>
          </FormField>

          <RadioGroup<GlobalDictionarySourceKind>
            name="global-dictionary-source-kind"
            legend={t('admin.globalDictionaries.form.sourceKind')}
            value={sourceKind}
            onChange={(value) => {
              setSourceKind(value);
              if (value === 'manual') {
                setSourceKey(null);
                setSourceSql('');
                setSourceParams([]);
              }
            }}
            options={[
              {
                value: 'manual',
                label: t('admin.globalDictionaries.form.sourceKindManual'),
                disabled: isStructureLocked,
              },
              {
                value: 'external',
                label: t('admin.globalDictionaries.form.sourceKindExternal'),
                disabled: isStructureLocked,
              },
            ]}
            data-testid="global-dictionary-form-source-kind"
          />

          {sourceKind === 'external' && (
            <div className="space-y-3">
              <RadioGroup<SourceVariant>
                name="global-dictionary-source-variant"
                legend={t('admin.globalDictionaries.form.sourceVariant')}
                value={sourceVariant}
                onChange={handleVariantChange}
                options={[
                  {
                    value: 'registry',
                    label: t('admin.globalDictionaries.form.sourceVariantRegistry'),
                    disabled: isStructureLocked,
                  },
                  {
                    value: 'sql',
                    label: t('admin.globalDictionaries.form.sourceVariantSql'),
                    disabled: isStructureLocked,
                  },
                ]}
                data-testid="global-dictionary-form-source-variant"
              />

              {sourceVariant === 'registry' && (
                <>
                  <FormField
                    label={t('admin.globalDictionaries.form.sourcePicker')}
                    hint={
                      sourcesQuery.isError
                        ? undefined
                        : t('admin.globalDictionaries.form.sourcePickerHint')
                    }
                    error={
                      sourcesQuery.isError
                        ? t('admin.globalDictionaries.form.sourcePickerLoadError')
                        : undefined
                    }
                  >
                    <Dropdown
                      value={sourceKey ?? undefined}
                      onChange={(value) => setSourceKey(value === '' ? null : value)}
                      disabled={isStructureLocked || sourcesQuery.isLoading}
                      placeholder={t('admin.globalDictionaries.form.sourcePickerPlaceholder')}
                      options={sources.map((source) => ({
                        value: source.key,
                        label: t(source.nameI18nKey),
                      }))}
                      data-testid="global-dictionary-form-source-picker"
                    />
                  </FormField>

                  {sourceKey && (
                    <ExternalSourceInfoPanel source={selectedSource} sourceKey={sourceKey} />
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {sqlVariantActive && (
          <>
            {/* Pole SQL na całą szerokość okna (obie kolumny). */}
            <div className="space-y-1 lg:col-span-2">
              <div className="flex justify-end">
                <InsertExampleButton
                  currentValue={sourceSql}
                  example={t('admin.globalDictionaries.form.sqlExample')}
                  onInsert={setSourceSql}
                  disabled={isStructureLocked}
                  data-testid="global-dictionary-form-sql-example"
                />
              </div>
              <FormField
                label={t('admin.globalDictionaries.form.sqlLabel')}
                hint={
                  testTenantId
                    ? t('admin.globalDictionaries.form.sqlHint')
                    : `${t('admin.globalDictionaries.form.sqlHint')} ${t('admin.globalDictionaries.form.sqlSchemaHint')}`
                }
              >
                <SqlField
                  value={sourceSql}
                  onChange={(value) => setSourceSql(value)}
                  schema={sqlSchema}
                  parameters={sqlParams}
                  diagnostics={sqlDiagnostics}
                  readOnly={isStructureLocked}
                  placeholder={t('common.examplePlaceholder', {
                    example: t('admin.globalDictionaries.form.sqlExample'),
                  })}
                  aria-label={t('admin.globalDictionaries.form.sqlLabel')}
                  minHeight="200px"
                  data-testid="global-dictionary-form-sql"
                />
              </FormField>
            </div>

            <FormField
              label={t('admin.globalDictionaries.form.sqlParamsTitle')}
              hint={t('admin.globalDictionaries.form.sqlParamsHint')}
              error={errors.sourceParams}
            >
              <div className="space-y-2" data-testid="global-dictionary-form-sql-params">
                {sourceParams.map((param, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={param.name}
                      onChange={(e) => {
                        updateParam(index, { name: e.target.value });
                        setErrors((prev) => ({ ...prev, sourceParams: '' }));
                      }}
                      disabled={isStructureLocked}
                      placeholder={t('admin.globalDictionaries.form.sqlParamNamePlaceholder')}
                      containerClassName="min-w-0 flex-1"
                      data-testid={`global-dictionary-form-sql-param-name-${index}`}
                    />
                    <Dropdown
                      className="w-32 shrink-0"
                      value={param.type}
                      onChange={(value) =>
                        updateParam(index, {
                          type: value as DictionarySourceParamType,
                        })
                      }
                      disabled={isStructureLocked}
                      options={DICTIONARY_SOURCE_PARAM_TYPES.map((type) => ({
                        value: type,
                        label: t(`admin.globalDictionaries.form.paramType.${type}`),
                      }))}
                      data-testid={`global-dictionary-form-sql-param-type-${index}`}
                    />
                    <label className="flex shrink-0 items-center gap-1 text-xs text-text-secondary">
                      <input
                        type="checkbox"
                        checked={param.required}
                        onChange={(e) => updateParam(index, { required: e.target.checked })}
                        disabled={isStructureLocked}
                        data-testid={`global-dictionary-form-sql-param-required-${index}`}
                      />
                      {t('admin.globalDictionaries.form.paramRequired')}
                    </label>
                    <button
                      type="button"
                      onClick={() => removeParam(index)}
                      disabled={isStructureLocked}
                      aria-label={t('admin.globalDictionaries.form.sqlParamRemove')}
                      className="rounded p-1.5 text-text-muted transition hover:bg-error-500/10 hover:text-error-500"
                      data-testid={`global-dictionary-form-sql-param-remove-${index}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Plus className="h-3.5 w-3.5" />}
                  onClick={() =>
                    setSourceParams((prev) => [
                      ...prev,
                      { name: '', type: 'text', required: false },
                    ])
                  }
                  disabled={isStructureLocked}
                  data-testid="global-dictionary-form-sql-param-add"
                >
                  {t('admin.globalDictionaries.form.sqlParamAdd')}
                </Button>
              </div>
            </FormField>

            {/* Sekcja „Testuj" — wykonanie zapytania na wybranym tenancie (rola RO). */}
            <div
              className="space-y-2 rounded-lg border border-border-primary bg-bg-secondary p-3"
              data-testid="global-dictionary-form-test-section"
            >
              <p className="text-sm font-medium text-text-primary">
                {t('admin.globalDictionaries.form.testTitle')}
              </p>
              <FormField
                label={t('admin.globalDictionaries.form.testTenant')}
                error={
                  tenantsQuery.isError
                    ? t('admin.globalDictionaries.form.testTenantsLoadError')
                    : undefined
                }
              >
                <Dropdown
                  value={testTenantId === '' ? undefined : testTenantId}
                  onChange={(value) => setTestTenantId(value)}
                  disabled={tenantsQuery.isLoading}
                  placeholder={t('admin.globalDictionaries.form.testTenantPlaceholder')}
                  options={tenants.map((tenant) => ({
                    value: tenant.id,
                    label: `${tenant.name} (${tenant.slug})`,
                  }))}
                  data-testid="global-dictionary-form-test-tenant"
                />
              </FormField>
              {sourceParams.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-text-muted">
                    {t('admin.globalDictionaries.form.testParamValuesHint')}
                  </p>
                  {sourceParams
                    .filter((param) => param.name.trim())
                    .map((param) => (
                      <FormField key={param.name} label={param.name}>
                        <Input
                          value={testParamValues[param.name] ?? ''}
                          onChange={(e) =>
                            setTestParamValues((prev) => ({
                              ...prev,
                              [param.name]: e.target.value,
                            }))
                          }
                          data-testid={`global-dictionary-form-test-param-${param.name}`}
                        />
                      </FormField>
                    ))}
                </div>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={handleTest}
                loading={testMutation.isPending}
                disabled={testMutation.isPending || !testTenantId || sourceSql.trim().length === 0}
                data-testid="global-dictionary-form-test-btn"
              >
                {t('admin.globalDictionaries.form.testButton')}
              </Button>

              {testError && (
                <p
                  role="alert"
                  className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
                  data-testid="global-dictionary-form-test-error"
                >
                  {testError}
                </p>
              )}

              {testResult && (
                <div className="space-y-2" data-testid="global-dictionary-form-test-result">
                  <p className="text-xs text-text-secondary">
                    {t('admin.globalDictionaries.form.testRowCount', {
                      count: testResult.rowCount,
                    })}
                  </p>
                  {testResult.truncated && (
                    <p
                      className="text-xs text-amber-600 dark:text-amber-400"
                      data-testid="global-dictionary-form-test-truncated"
                    >
                      {t('admin.globalDictionaries.form.testTruncated')}
                    </p>
                  )}
                  {testResult.rows.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="border-b border-border-primary text-left text-text-muted">
                          <tr>
                            {testResult.columns.map((column) => (
                              <th key={column} className="px-2 py-1 font-medium">
                                {column}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border-primary">
                          {testResult.rows.slice(0, 20).map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {testResult.columns.map((column) => (
                                <td key={column} className="px-2 py-1 text-text-secondary">
                                  {row[column] === null || row[column] === undefined
                                    ? '—'
                                    : String(row[column])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
